import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
} from '@mui/material';
import {
  People as Users,
  Public as Globe2,
  EmojiEvents as Award,
  TrendingUp,
  Favorite as Heart,
  Security as Shield,
  Message as MessageSquare,
  AccessTime as Clock,
  Star,
  CheckCircle,
  Business as Building2,
  Phone,
  Mail,
  LocationOn as MapPin,
  Lock,
  ThumbUp as ThumbsUp,
  TrackChanges as Target,
  RocketLaunch as Rocket,
  Handshake,
  TrendingDown,
  FlashOn as Zap,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const AboutUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const stats = [
    {
      number: '5.5L+',
      label: 'Happy Travelers',
      icon: <Users sx={{ fontSize: 40, color: '#FF7700' }} />,
      description: 'Satisfied customers across India',
      color: 'from-blue-500 to-blue-600',
    },
    {
      number: '5000+',
      label: 'Partner Hotels',
      icon: <Building2 sx={{ fontSize: 40, color: '#FF7700' }} />,
      description: 'Verified premium properties',
      color: 'from-green-500 to-green-600',
    },
    {
      number: '90+',
      label: 'Major Cities',
      icon: <Globe2 sx={{ fontSize: 40, color: '#FF7700' }} />,
      description: 'Expanding nationwide coverage',
      color: 'from-purple-500 to-purple-600',
    },
    {
      number: '24/7',
      label: 'Support',
      icon: <MessageSquare sx={{ fontSize: 40, color: '#FF7700' }} />,
      description: 'Always here to help you',
      color: 'from-orange-500 to-orange-600',
    },
  ];

  const coreValues = [
    {
      icon: <Users sx={{ fontSize: 32 }} />,
      title: 'Customer First',
      description:
        'Our customers are at the heart of everything we do. We prioritize their needs and strive to exceed expectations with every interaction.',
      color: 'from-blue-500 to-blue-600',
    },
    {
      icon: <Shield sx={{ fontSize: 32 }} />,
      title: 'Trust & Transparency',
      description:
        'We believe in building lasting relationships through honest communication, fair practices, and complete transparency in all our dealings.',
      color: 'from-green-500 to-green-600',
    },
    {
      icon: <Award sx={{ fontSize: 32 }} />,
      title: 'Quality Assurance',
      description:
        'We maintain the highest standards in property verification, service delivery, and customer support to ensure exceptional experiences.',
      color: 'from-purple-500 to-purple-600',
    },
    {
      icon: <Heart sx={{ fontSize: 32 }} />,
      title: 'Passion for Service',
      description:
        'Our team is driven by a genuine passion for hospitality and travel, ensuring every booking is handled with care and attention to detail.',
      color: 'from-red-500 to-red-600',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 32 }} />,
      title: 'Continuous Innovation',
      description:
        'We constantly evolve our platform with new features, technologies, and services to stay ahead and provide the best booking experience.',
      color: 'from-orange-500 to-orange-600',
    },
    {
      icon: <Globe2 sx={{ fontSize: 32 }} />,
      title: 'Global Reach',
      description:
        'While we focus on India, we maintain international standards and partnerships to offer world-class hospitality experiences.',
      color: 'from-indigo-500 to-indigo-600',
    },
    {
      icon: <MessageSquare sx={{ fontSize: 32 }} />,
      title: 'Open Communication',
      description:
        'We maintain open channels of communication with our partners and customers, ensuring quick resolution and continuous improvement.',
      color: 'from-pink-500 to-pink-600',
    },
    {
      icon: <Clock sx={{ fontSize: 32 }} />,
      title: 'Reliability',
      description:
        'You can count on us for consistent service, timely payments, and dependable support whenever you need assistance.',
      color: 'from-cyan-500 to-cyan-600',
    },
    {
      icon: <Lock sx={{ fontSize: 32 }} />,
      title: 'Secure Transactions',
      description:
        'We use industry-standard encryption and secure payment gateways to protect all financial transactions and personal data.',
      color: 'from-teal-500 to-teal-600',
    },
  ];

  const trustFeatures = [
    {
      icon: <Shield sx={{ fontSize: 48, color: '#4CAF50' }} />,
      title: '100% Secure Booking',
      description:
        'Your payments and personal data are protected with industry-standard SSL encryption and secure payment gateways',
      badge: 'SSL Secured',
    },
    {
      icon: <Award sx={{ fontSize: 48, color: '#2196F3' }} />,
      title: 'Best Price Guarantee',
      description:
        "Find a lower price elsewhere? We'll match it and give you an additional 10% discount on your booking",
      badge: 'Price Match',
    },
    {
      icon: <ThumbsUp sx={{ fontSize: 48, color: '#F44336' }} />,
      title: 'Trusted by Travelers',
      description:
        'Join 5.5 lakhs+ happy customers who choose TabTrips for their perfect stay. Experience premium hospitality with verified properties and exceptional service',
      badge: '5.5L+ Users',
    },
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      location: 'Mumbai',
      rating: 5,
      text: 'TabTrips made my business trip seamless. The hotel was exactly as described, and the booking process was incredibly smooth. Highly recommended!',
      avatar: 'RK',
    },
    {
      name: 'Priya Sharma',
      location: 'Delhi',
      rating: 5,
      text: 'Best prices I found anywhere! The customer support team was very helpful when I needed to modify my booking. Will definitely use again.',
      avatar: 'PS',
    },
    {
      name: 'Amit Patel',
      location: 'Bangalore',
      rating: 5,
      text: 'Verified properties, secure payments, and great deals. TabTrips has become my go-to platform for all hotel bookings. Excellent service!',
      avatar: 'AP',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9f9f9' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #FF7700 0%, #ff8800 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          overflow: 'hidden',
        }}
      >
        {/* Background Image Overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/banner/main.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.4))',
          }}
        />

        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '400px',
            height: '400px',
            bgcolor: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            filter: 'blur(80px)',
            transform: 'translate(30%, -30%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '300px',
            height: '300px',
            bgcolor: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            transform: 'translate(-30%, 30%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Chip
              icon={<Star sx={{ color: '#FFD700 !important' }} />}
              label="Trusted by 5.5 Lakhs+ Travelers"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                mb: 3,
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              Our Story
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1rem', md: '1.5rem' },
                maxWidth: '800px',
                mx: 'auto',
                opacity: 0.95,
                lineHeight: 1.6,
              }}
            >
              TabTrips is India's trusted platform for premium hotel bookings. We connect travelers
              with verified properties, offering the best prices, secure payments, and exceptional
              service across 90+ cities nationwide.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ mt: 4 }}
            >
              <Chip
                icon={<CheckCircle />}
                label="Verified Properties"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                }}
              />
              <Chip
                icon={<Shield />}
                label="Secure Payments"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                }}
              />
              <Chip
                icon={<Clock />}
                label="24/7 Support"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                }}
              />
            </Stack>
          </motion.div>
        </Container>
      </Box>

      {/* Statistics Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'white', mt: -4, position: 'relative', zIndex: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      borderRadius: '20px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        {stat.icon}
                      </Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          background: 'linear-gradient(135deg, #2E4057 0%, #1a2838 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#2E4057' }}>
                        {stat.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {stat.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Mission & Vision */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 2,
                color: '#2E4057',
              }}
            >
              Our Purpose
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Driving our commitment to excellence and customer satisfaction
            </Typography>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 4, md: 6 },
                    borderRadius: '24px',
                    background: 'linear-gradient(135deg, #fff5e6 0%, #ffe8cc 100%)',
                    border: '1px solid rgba(255,119,0,0.2)',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '150px',
                      height: '150px',
                      bgcolor: 'rgba(255,119,0,0.1)',
                      borderRadius: '50%',
                      filter: 'blur(60px)',
                      transform: 'translate(30%, -30%)',
                    }}
                  />
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #FF7700 0%, #ff8800 100%)',
                        color: 'white',
                        mb: 3,
                      }}
                    >
                      <Target sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 700, mb: 3, color: '#2E4057', fontSize: { xs: '1.75rem', md: '2.25rem' } }}
                    >
                      Our Mission
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555' }}
                    >
                      To make premium hotel bookings accessible and affordable for everyone in India.
                      We strive to connect travelers with verified properties while ensuring the best
                      prices, secure transactions, and exceptional customer service at every step of
                      their journey.
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 4, md: 6 },
                    borderRadius: '24px',
                    background: 'linear-gradient(135deg, #e6f3ff 0%, #cce7ff 100%)',
                    border: '1px solid rgba(33,150,243,0.2)',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '150px',
                      height: '150px',
                      bgcolor: 'rgba(33,150,243,0.1)',
                      borderRadius: '50%',
                      filter: 'blur(60px)',
                      transform: 'translate(30%, -30%)',
                    }}
                  />
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                        color: 'white',
                        mb: 3,
                      }}
                    >
                      <Rocket sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 700, mb: 3, color: '#2E4057', fontSize: { xs: '1.75rem', md: '2.25rem' } }}
                    >
                      Our Vision
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#555' }}
                    >
                      To become India's most trusted and preferred hotel booking platform, known for
                      innovation, reliability, and customer-centricity. We envision a future where
                      every traveler can easily find and book their perfect stay with confidence and
                      peace of mind.
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CEO Message */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#f9f9f9' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '24px',
                bgcolor: 'white',
                boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '200px',
                  height: '200px',
                  bgcolor: 'rgba(255,119,0,0.05)',
                  borderRadius: '50%',
                  filter: 'blur(80px)',
                  transform: 'translate(-50%, -50%)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: '200px',
                  height: '200px',
                  bgcolor: 'rgba(255,119,0,0.05)',
                  borderRadius: '50%',
                  filter: 'blur(80px)',
                  transform: 'translate(50%, 50%)',
                }}
              />
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Chip
                  label="Message from Leadership"
                  sx={{
                    bgcolor: '#fff5e6',
                    color: '#FF7700',
                    fontWeight: 600,
                    mb: 3,
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 4,
                    color: '#2E4057',
                    fontSize: { xs: '1.75rem', md: '2.25rem' },
                  }}
                >
                  A Word from Our CEO
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    lineHeight: 1.8,
                    color: '#555',
                    fontStyle: 'italic',
                    mb: 4,
                    px: { xs: 2, md: 4 },
                  }}
                >
                  "Travel is not just about reaching a destination; it's about creating memories that
                  last a lifetime. At TabTrips, we believe every journey should be extraordinary, and
                  every stay should feel like home. Our commitment is simple: to make premium travel
                  accessible to all, with trust, transparency, and exceptional service at the heart of
                  everything we do."
                </Typography>
                <Divider sx={{ my: 4 }} />
                <Box
                  sx={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FF7700 0%, #ff8800 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 4px 20px rgba(255,119,0,0.3)',
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                    PK
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#2E4057' }}>
                  Parveen Kumar
                </Typography>
                <Typography variant="h6" sx={{ color: '#FF7700', fontWeight: 600, mb: 2 }}>
                  Chief Executive Officer & Founder
                </Typography>
                <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ mb: 2 }}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} sx={{ color: '#FFD700', fontSize: 20 }} />
                  ))}
                </Stack>
                <Box
                  sx={{
                    width: 120,
                    height: 4,
                    mx: 'auto',
                    borderRadius: '2px',
                    background: 'linear-gradient(90deg, #FF7700 0%, #ff8800 100%)',
                  }}
                />
              </Box>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Core Values */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 2,
                color: '#2E4057',
              }}
            >
              Core Values
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              The principles that guide everything we do
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {coreValues.map((value, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: '20px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      border: '1px solid rgba(0,0,0,0.05)',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                        borderColor: '#FF7700',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 56,
                        height: 56,
                        borderRadius: '12px',
                        background: `linear-gradient(135deg, #FF7700 0%, #ff8800 100%)`,
                        color: 'white',
                        mb: 2,
                      }}
                    >
                      {value.icon}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#2E4057' }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {value.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Trust Indicators */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#f9f9f9' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 2,
                color: '#2E4057',
              }}
            >
              Why Trust TabTrips?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Join thousands of satisfied travelers who trust us for their perfect stay
            </Typography>
          </Box>
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {trustFeatures.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      textAlign: 'center',
                      p: 4,
                      borderRadius: '20px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(0,0,0,0.05)',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                        p: 2,
                        borderRadius: '16px',
                        bgcolor: '#f5f5f5',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Chip
                      label={feature.badge}
                      sx={{
                        bgcolor: '#fff5e6',
                        color: '#FF7700',
                        fontWeight: 600,
                        mb: 2,
                      }}
                    />
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#2E4057' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3}>
            {[
              { icon: <CheckCircle sx={{ fontSize: 32, color: '#4CAF50' }} />, text: 'Verified Properties' },
              { icon: <Lock sx={{ fontSize: 32, color: '#2196F3' }} />, text: 'Secure Payments' },
              { icon: <Clock sx={{ fontSize: 32, color: '#FF7700' }} />, text: '24/7 Support' },
              { icon: <TrendingDown sx={{ fontSize: 32, color: '#9C27B0' }} />, text: 'Best Prices' },
            ].map((item, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    borderRadius: '16px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(0,0,0,0.05)',
                  }}
                >
                  {item.icon}
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, color: '#2E4057' }}>
                    {item.text}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 2,
                color: '#2E4057',
              }}
            >
              What Our Customers Say
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Real experiences from real travelers
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: '20px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      border: '1px solid rgba(0,0,0,0.05)',
                      height: '100%',
                    }}
                  >
                    <Stack direction="row" spacing={0.5} sx={{ mb: 3 }}>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} sx={{ color: '#FFD700', fontSize: 20 }} />
                      ))}
                    </Stack>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#555',
                        mb: 3,
                        lineHeight: 1.7,
                        fontStyle: 'italic',
                      }}
                    >
                      "{testimonial.text}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #FF7700 0%, #ff8800 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 700,
                        }}
                      >
                        {testimonial.avatar}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2E4057' }}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.location}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact CTA */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: 'linear-gradient(135deg, #FF7700 0%, #ff8800 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '3rem' },
                  mb: 3,
                }}
              >
                Have Questions? We're Here to Help!
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  mb: 4,
                  opacity: 0.95,
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                Our team is available 24/7 to assist you with any questions or concerns about your
                booking.
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
              >
                <Box
                  component="button"
                  onClick={() => navigate('/support')}
                  sx={{
                    bgcolor: 'white',
                    color: '#FF7700',
                    px: 4,
                    py: 2,
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 25px rgba(0,0,0,0.25)',
                    },
                  }}
                >
                  Contact Us
                </Box>
                <Box
                  component="a"
                  href="tel:+917082828181"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    px: 4,
                    py: 2,
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    transition: 'all 0.3s ease',
                    border: '2px solid white',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.3)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Phone />
                  Call Us Now
                </Box>
              </Stack>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUs;

